import { Box } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { ColorIcon, Spacing } from "goi_common";
import { BaseButton, BaseStack, baseColors } from "goi_common/src/design-system";

import QuestionBottomCta, { QUESTION_BOTTOM_CTA_SPACE_VERTICAL } from "./QuestionBottomCta";
import QuestionTopBar from "./QuestionTopBar";
import UrgentCallButton from "./UrgentCallButton";

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * 조문객 무리. x 는 가운데에서 떨어진 거리(px). 뒤로 갈수록 작고 높고 흐리다.
 * 얼굴은 다 다르게(같은 얼굴이 반복돼 보이지 않게), 둘째 줄은 맨 뒷줄보다 짙게(2026-11-05 주완)
 */
const CROWD: {
  icon:
    | "icon_Graphic_Suit"
    | "icon_Graphic_GuestFemale"
    | "icon_Graphic_GuestFemaleLong"
    | "icon_Graphic_GuestSenior"
    | "icon_Graphic_GuestGlasses";
  x: number;
  bottom: number;
  size: number;
  z: number;
  opacity: number;
}[] = [
  { icon: "icon_Graphic_GuestSenior", x: -96, bottom: 38, size: 72, z: 1, opacity: 0.28 },
  { icon: "icon_Graphic_GuestFemaleLong", x: 96, bottom: 38, size: 72, z: 1, opacity: 0.28 },
  { icon: "icon_Graphic_GuestFemale", x: -52, bottom: 18, size: 88, z: 2, opacity: 0.5 },
  { icon: "icon_Graphic_GuestGlasses", x: 52, bottom: 18, size: 88, z: 2, opacity: 0.5 },
  { icon: "icon_Graphic_Suit", x: 0, bottom: 0, size: 130, z: 3, opacity: 1 },
];

/**
 * E5 첫 화면 다음, 조문객 질문 앞에 두는 「왜 조문객 수를 묻는지」 한 장(2026-11-05 주완).
 * 설명 없이 바로 물으면 「이걸 왜 묻지」 에서 흐름이 끊긴다. 예전 장례식장 견적에서 빈소 평형을 설명 없이 물었을 때
 * 가장 어려워했고 많이 떠났다(#129 기록).
 * 여기서는 진짜 쉽게, 제목 · 한 줄 · 조문객 그림만. 항목별로 무엇이 달라지는지는 뒤의 비용 구성 화면에서 보여 준다(주완).
 */
export default function GuestWhyStep({
  onNext,
  onBack,
  onUrgent,
}: {
  onNext: () => void;
  onBack: () => void;
  onUrgent: () => void;
}) {
  const reduceMotion = !!useReducedMotion();

  return (
    <BaseStack width="100%" bg={baseColors.background_normal}>
      <QuestionTopBar onBack={onBack} />
      <BaseStack width="100%" px="20px">
        <Spacing gap={12} />
        <Box as="h2" className="title_3_b" color={baseColors.label_normal} wordBreak="keep-all">
          구체적인 비용을 알기 위해선
          <br />
          <Box as="span" color={baseColors.primary_orange}>
            조문객 수
          </Box>
          가 필요해요
        </Box>
        <Spacing gap={8} />
        <Box className="body_1_normal_r" color={baseColors.label_assistive_2} wordBreak="keep-all">
          조문객이 많을수록 식사와 빈소가 커져서
          <br />
          비용이 크게 달라져요
        </Box>

        {/*
          조문객 그림: 앞에 정장 조문객 한 명 + 뒤에 흐린 무리(보드 「P3 앞 조문객 그림」 A, 2026-11-05 주완).
          상주 그림은 완장이 있어 「우리 가족」 으로 읽혀서 바꿨다. 뒤를 회색으로 흐려 같은 얼굴이 반복돼 보이지 않게 한다
        */}
        <Spacing gap={56} />
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5, ease: EASE }}
          style={{ position: "relative", width: "100%", height: 200 }}
        >
          {CROWD.map((c, i) => (
            <Box
              key={i}
              position="absolute"
              left={`calc(50% + ${c.x}px)`}
              bottom={`${c.bottom}px`}
              transform="translateX(-50%)"
              zIndex={c.z}
              filter={c.opacity < 1 ? "grayscale(1)" : undefined}
              opacity={c.opacity}
              display="flex"
            >
              <ColorIcon name={c.icon} size={c.size} />
            </Box>
          ))}
        </motion.div>
      </BaseStack>

      <Spacing gap={QUESTION_BOTTOM_CTA_SPACE_VERTICAL} />
      <QuestionBottomCta>
        <BaseButton type="primary" size="large" onClick={onNext}>
          <Box className="body_1_normal_m">다음</Box>
        </BaseButton>
        <Spacing gap={8} />
        <UrgentCallButton onClick={onUrgent} />
      </QuestionBottomCta>
    </BaseStack>
  );
}
